import {IconButton} from '@dropbox/dig-components/buttons';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CalendarLine, GoogleExternalLogo, VideoLine} from '@dropbox/dig-icons/assets';
import {ButtonLink, Link} from 'components/DSYS/Link';
import {t} from 'i18next';
import {
  extractAgendaDescriptionLinks,
  resolveAgendaJoinUrl,
} from 'views/home/dashboard/tiles/agendaDescriptionLinks';
import {AgendaLinkChip} from 'views/home/dashboard/tiles/AgendaLinkChip';

import {Meeting} from './useMeetings';

/** Links from the invite beyond Join and the calendar (agendas, docs); the rest stay in the invite. */
const MAX_INVITE_LINKS = 4;
/** Each invite link has a line to itself, so its name can run wider than the tile's. */
const INVITE_LABEL_MAX_WIDTH = 320;

const isGoogleCalendar = (url: string) => {
  try {
    return new URL(url).hostname === 'calendar.google.com';
  } catch {
    return false;
  }
};

const joinUrlOf = (meeting: Meeting) =>
  resolveAgendaJoinUrl(meeting.joinUrl ?? undefined, meeting.description ?? undefined);

/** The event in your calendar, as just an icon (the agenda tile's old button). */
const CalendarButton = ({url}: {url: string}) => {
  const label = t('search_box_meeting_calendar');
  return (
    <Tooltip placement="top" title={label}>
      <Link hasNoUnderline to={url} aria-label={label}>
        <IconButton tabIndex={-1} variant="opacity" size="small" aria-hidden>
          <UIIcon src={isGoogleCalendar(url) ? GoogleExternalLogo : CalendarLine} size="small" />
        </IconButton>
      </Link>
    </Tooltip>
  );
};

/** The calendar icon then Join, as items for the Split they sit in (the time line). */
export const MeetingButtons = ({meeting}: {meeting: Meeting}) => {
  const joinUrl = joinUrlOf(meeting);
  return (
    <>
      {meeting.calendarUrl && (
        <Split.Item>
          <CalendarButton url={meeting.calendarUrl} />
        </Split.Item>
      )}
      {joinUrl && (
        <Split.Item>
          <ButtonLink
            to={joinUrl}
            variant="primary"
            size="small"
            withIconStart={<UIIcon src={VideoLine} size="small" />}
          >
            {t('search_box_meeting_join')}
          </ButtonLink>
        </Split.Item>
      )}
    </>
  );
};

/** The invite's links (agendas, docs), one per line, as the agenda tile's chips. */
export const InviteLinks = ({meeting}: {meeting: Meeting}) => {
  const links = extractAgendaDescriptionLinks(meeting.description ?? undefined, {
    excludeUrls: [joinUrlOf(meeting), meeting.calendarUrl].filter((url): url is string =>
      Boolean(url)
    ),
  }).slice(0, MAX_INVITE_LINKS);
  if (!links.length) return null;

  return (
    <Stack gap="4" align="start">
      {links.map(({url, label}) => (
        <AgendaLinkChip
          key={url}
          url={url}
          label={label}
          isLive={false}
          maxLabelWidth={INVITE_LABEL_MAX_WIDTH}
        />
      ))}
    </Stack>
  );
};
